import React, { useEffect, useState } from 'react'

import { SortableHandle } from 'react-sortable-hoc'

import { DeleteIcon } from '~/client/src/shared/components/icons/DeleteIcon'
import { DragIcon } from '~/client/src/shared/components/icons/DragIcon'
import { EditIcon } from '~/client/src/shared/components/icons/EditIcon'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import Localization from '~/client/src/shared/localization/LocalizationManager'

interface ISelectOptionItemProps {
  option: { value: string; label: string }
  optionIndex: number
  isEditing: boolean
  showDragHandle?: boolean
  onStartEdit: (index: number) => void
  onEdit: (index: number, newLabel: string) => void
  onCancelEdit: () => void
  onDelete: (index: number) => void
}

const DragHandle = SortableHandle(() => (
  <div
    onClick={(e: React.MouseEvent) => e.stopPropagation()}
    className="tw:flex tw:items-center tw:text-muted-foreground tw:cursor-grab active:tw:cursor-grabbing"
    tabIndex={0}
  >
    <DragIcon size="sm" className="tw:pointer-events-none tw:shrink-0" />
  </div>
))

const SelectOptionItem: React.FC<ISelectOptionItemProps> = ({
  option,
  optionIndex,
  isEditing,
  showDragHandle = true,
  onStartEdit,
  onEdit,
  onCancelEdit,
  onDelete,
}) => {
  const [editValue, setEditValue] = useState(option.label)

  useEffect(() => {
    if (isEditing) {
      setEditValue(option.label)
    }
  }, [isEditing, option.label])

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onStartEdit(optionIndex)
  }

  const handleCancel = () => {
    setEditValue(option.label)
    onCancelEdit()
  }

  const handleSave = () => {
    onEdit(optionIndex, editValue)
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete(optionIndex)
  }

  if (isEditing) {
    return (
      <div className="tw:flex tw:items-center tw:gap-2 tw:px-3 tw:py-2">
        {showDragHandle && <DragHandle />}
        <div className="tw:flex tw:flex-1 tw:items-baseline tw:gap-2">
          <StruxhubInput
            value={editValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEditValue(e.target.value)
            }
            isLabelHidden={true}
            isHelperTextHidden={true}
            isHelperTextCondensed={true}
            autoFocus
          />
          <div className="tw:flex tw:gap-2">
            <Button variant="outline" onClick={handleCancel}>
              {Localization.translator.cancel}
            </Button>
            <Button variant="default" onClick={handleSave}>
              {Localization.translator.save}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="tw:group tw:flex tw:items-center tw:gap-2 tw:px-3 tw:py-2 tw:rounded-md tw:text-body-md tw:text-foreground hover:tw:bg-muted">
      {showDragHandle && <DragHandle />}
      <span className="tw:flex-1">{option.label}</span>
      <div className="tw:flex tw:gap-1 tw:opacity-0 tw:transition-opacity tw:duration-200 tw:group-hover:opacity-100">
        <button
          type="button"
          className="tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:border-0 tw:bg-transparent tw:p-1 tw:rounded tw:transition-colors hover:tw:bg-accent"
          onClick={handleEditClick}
        >
          <EditIcon size="sm" className="tw:text-muted-foreground" />
        </button>
        <button
          type="button"
          className="tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:border-0 tw:bg-transparent tw:p-1 tw:rounded tw:transition-colors hover:tw:bg-accent"
          onClick={handleDelete}
        >
          <DeleteIcon size="sm" className="tw:text-destructive" />
        </button>
      </div>
    </div>
  )
}

export default SelectOptionItem
